import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "../hooks/useDashboard";
import Overview from "../components/Dashboard/Overview";
import UserActivity from "../components/Dashboard/UserActivity";
import PatientStatistics from "../components/Dashboard/PatientStatistics";
import { StatisticsSkeleton } from "../components/Skeletons";
import {
  canViewEmployeeStatistics,
  canViewPatientStatistics,
  canAccessDashboard,
  defaultDashboardTabForRole,
} from "../utils/moduleAccess";
import {
  NurseIcon,
  RunningIcon,
  DoctorIcon,
  CommentsIcon,
  HelpingIcon,
  MedicalNotesIcon,
  BriefcaseIcon,
  DashboardIcon,
  ClockIcon,
  CalendarIcon,
  UsersIcon,
} from "../Icons";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
} from "../ui/pageChrome";
import { BRAND_THEME } from "../ui/brandTheme";
import ModuleTabPanel from "../ui/ModuleTabPanel";
import StatCard from "../ui/StatCard";

const POSITIONS = [
  { name: "RN", icon: NurseIcon },
  { name: "PT", icon: RunningIcon },
  { name: "PTA", icon: RunningIcon },
  { name: "OT", icon: DoctorIcon },
  { name: "OTA", icon: DoctorIcon },
  { name: "ST", icon: CommentsIcon },
  { name: "HHA", icon: MedicalNotesIcon },
  { name: "LVN", icon: NurseIcon },
  { name: "MSW", icon: HelpingIcon },
  { name: "Caregiver", code: "CG", icon: BriefcaseIcon },
  { name: "CNA", code: "CNA", icon: BriefcaseIcon },
  { name: "Office Assistant", code: "OA", icon: BriefcaseIcon },
  { name: "ADMIN", icon: DoctorIcon },
  { name: "Surveyor", code: "SRVY", icon: DoctorIcon },
  { name: "Contractor", code: "CON", icon: BriefcaseIcon },
];

const Dashboard = () => {
  const { stats, loading, activeTab, setActiveTab } = useDashboard();
  const navigate = useNavigate();

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const hasDashboard = canAccessDashboard(currentUser);
  const showEmployeeStats = canViewEmployeeStatistics(currentUser);
  const showPatientStats = canViewPatientStatistics(currentUser);
  const fallbackTab = defaultDashboardTabForRole(currentUser);

  useEffect(() => {
    if (!hasDashboard) return;
    if (activeTab === "Dashboard" && !showEmployeeStats) {
      setActiveTab(fallbackTab);
    }
    if (activeTab === "PatientStats" && !showPatientStats) {
      setActiveTab(fallbackTab);
    }
  }, [
    activeTab,
    hasDashboard,
    showEmployeeStats,
    showPatientStats,
    fallbackTab,
    setActiveTab,
  ]);

  const tabs = useMemo(() => {
    const all = [
      {
        id: "Dashboard",
        label: "Employee Statistics",
        icon: DashboardIcon,
        visible: showEmployeeStats,
      },
      {
        id: "PatientStats",
        label: "Patient Statistics",
        icon: UsersIcon,
        visible: showPatientStats,
      },
      { id: "Overview", label: "Overview", icon: ClockIcon, visible: true },
      { id: "Activity", label: "Activity", icon: CalendarIcon, visible: true },
    ];
    return all.filter((tab) => tab.visible);
  }, [showEmployeeStats, showPatientStats]);

  const getCount = (key) => stats[key] ?? 0;

  const handleCardClick = (position) => {
    const code = position.code || position.name;
    navigate(`/employees?position=${encodeURIComponent(code)}`);
  };

  const subtitle =
    activeTab === "PatientStats"
      ? "Patient census, status distribution, and daily activity"
      : "Staff statistics, attendance overview, and time cards";

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${BRAND_THEME.section.title}`}>
            Dashboard
          </h1>
          <p className={PAGE_SUBTITLE}>{subtitle}</p>
        </div>
      </div>

      <ModuleTabPanel
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={BRAND_THEME}
      >
        <div className="animate-fade-in">
          {activeTab === "Activity" ? (
            <UserActivity />
          ) : activeTab === "Overview" ? (
            <Overview />
          ) : activeTab === "PatientStats" ? (
            <PatientStatistics />
          ) : loading ? (
            <StatisticsSkeleton />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {POSITIONS.map((pos) => (
                <StatCard
                  key={pos.name}
                  label={pos.name}
                  value={getCount(pos.code ?? pos.name)}
                  icon={pos.icon}
                  onClick={() => handleCardClick(pos)}
                />
              ))}
            </div>
          )}
        </div>
      </ModuleTabPanel>
    </div>
  );
};

export default Dashboard;
